import type { RouteRecord } from "vite-react-ssg";
import { lazy } from "react";
import { Navigate } from "react-router-dom";
import RootLayout from "./RootLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import { getAllArticles } from "@/lib/blog";
import { FLAGS } from "@/lib/featureFlags";

// Public marketing routes — eager imports so they're prerendered
// with full content. Each gets an `entry` so vite-react-ssg can
// resolve route-specific styles during the build.
import Index from "./pages/Index";
import ServicesPage from "./pages/ServicesPage";
import WorkPage from "./pages/WorkPage";
import PricingPage from "./pages/PricingPage";
import AboutPage from "./pages/AboutPage";
import Forbidden from "./pages/Forbidden";
import NotFound from "./pages/NotFound";
import Privacy from "./pages/Privacy";
import Cookies from "./pages/Cookies";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";

// Auth-gated and long-tail routes — lazy. They still get static
// HTML shells (vite-react-ssg renders whatever the Component returns
// at SSR time; auth-loading state will produce a spinner page),
// but the actual heavy bundle is deferred until navigation.
const Login = lazy(() => import("./pages/Login"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ClientPortal = lazy(() => import("./pages/ClientPortal"));
const BookPage = lazy(() => import("./pages/BookPage"));
const BookConsultation = lazy(() => import("./pages/BookConsultation"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const BookingConfirmed = lazy(() => import("./pages/BookingConfirmed"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const FormPage = lazy(() => import("./pages/FormPage"));
const SignPage = lazy(() => import("./pages/SignPage"));
const FillFormPage = lazy(() => import("./pages/FillFormPage"));
const ProposalPage = lazy(() => import("./pages/ProposalPage"));
const WebDesignOnboardingFormHE = lazy(() => import("./components/forms/he/WebDesignOnboardingFormHE"));
const SEOOnboardingFormHE = lazy(() => import("./components/forms/he/SEOOnboardingFormHE"));
const DiscoveryCallFormHE = lazy(() => import("./components/forms/he/DiscoveryCallFormHE"));
const HandoffChecklistHE = lazy(() => import("./components/forms/he/HandoffChecklistHE"));
const MonthlyRetainerReviewHE = lazy(() => import("./components/forms/he/MonthlyRetainerReviewHE"));

// Static paths for /blog/:slug — read at build time from the eager
// MDX glob in src/lib/blog.ts. Each article slug becomes a generated
// static HTML file at dist/blog/<slug>/index.html.
function blogStaticPaths(): string[] {
  return getAllArticles().map((a) => `blog/${a.frontmatter.slug}`);
}

export const routes: RouteRecord[] = [
  {
    path: "/",
    Component: RootLayout,
    children: [
      // ───── Public marketing routes (SSG) ─────
      { index: true, Component: Index, entry: "src/pages/Index.tsx" },
      { path: "services", Component: ServicesPage, entry: "src/pages/ServicesPage.tsx" },
      // /work prerendering disabled until showWorkSection flag is true.
      // Route entry stays so any incoming /work hit redirects home instead
      // of 404'ing. When flag flips on, restore the original line:
      // { path: "work", Component: WorkPage, entry: "src/pages/WorkPage.tsx" },
      {
        path: "work",
        element: FLAGS.showWorkSection ? <WorkPage /> : <Navigate to="/" replace />,
      },
      { path: "pricing", Component: PricingPage, entry: "src/pages/PricingPage.tsx" },
      { path: "packages", Component: PricingPage, entry: "src/pages/PricingPage.tsx" },
      { path: "about", Component: AboutPage, entry: "src/pages/AboutPage.tsx" },
      { path: "blog", Component: BlogPage, entry: "src/pages/BlogPage.tsx" },
      {
        path: "blog/:slug",
        Component: BlogPostPage,
        entry: "src/pages/BlogPostPage.tsx",
        getStaticPaths: blogStaticPaths,
      },
      { path: "book", Component: BookPage, entry: "src/pages/BookPage.tsx" },
      { path: "privacy", Component: Privacy, entry: "src/pages/Privacy.tsx" },
      { path: "cookies", Component: Cookies, entry: "src/pages/Cookies.tsx" },
      { path: "403", Component: Forbidden, entry: "src/pages/Forbidden.tsx" },

      // ───── Auth-gated and long-tail routes ─────
      // SSG still produces a shell HTML, but the auth/loading state means
      // the meaningful content only appears after client-side hydration.
      { path: "login", Component: Login },
      { path: "admin", element: <ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute> },
      { path: "portal/:clientId", element: <ProtectedRoute><ClientPortal /></ProtectedRoute> },
      { path: "dashboard", element: <ProtectedRoute><ClientPortal /></ProtectedRoute> },
      { path: "booking", Component: BookingPage },
      { path: "book-consultation", Component: BookConsultation },
      { path: "booking-confirmed", Component: BookingConfirmed },
      { path: "unsubscribe", Component: Unsubscribe },
      { path: "forms/web-design-onboarding-he", Component: WebDesignOnboardingFormHE },
      { path: "forms/seo-onboarding-he", Component: SEOOnboardingFormHE },
      { path: "forms/discovery-call-he", Component: DiscoveryCallFormHE },
      { path: "forms/handoff-he", Component: HandoffChecklistHE },
      { path: "forms/monthly-review-he", Component: MonthlyRetainerReviewHE },
      { path: "forms/:formSlug", Component: FormPage },
      { path: "sign/:token", Component: SignPage },
      { path: "fill/:token", Component: FillFormPage },
      { path: "proposal/:token", Component: ProposalPage },

      // 404 catch-all — kept client-only (no point prerendering arbitrary 404s).
      { path: "*", Component: NotFound },
    ],
  },
];
